'use client';

import { useEffect } from 'react';
import { useServiceConsentGranted } from '@/features/consent';
import { MATOMO_SERVICE_ID } from './MatomoTagManager';
import { trackMailtoClick } from './matomo-events';

/**
 * Reports clicks on `mailto:` links as a Matomo event. One delegated listener
 * covers every link — RichText, teasers, navigation — without turning
 * server-rendered markup into client components. It is only attached while the
 * visitor consents to Matomo, so revoking consent detaches it immediately.
 */
export function MailtoClickTracking() {
    const granted = useServiceConsentGranted(MATOMO_SERVICE_ID);

    useEffect(() => {
        if (!granted) return;

        // Capture phase: a handler that stops propagation further down cannot hide the click.
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [granted]);

    return null;
}

function handleClick(event: MouseEvent): void {
    if (isMailtoLink(event.target)) trackMailtoClick();
}

/** The click hit a `mailto:` link or something inside it, e.g. its icon. */
function isMailtoLink(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('a')?.protocol === 'mailto:';
}
